import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { formatMajorMoney, formatMinorMoney } from '@eventbrite/intl';
import isEmpty from 'lodash/isEmpty';
import isInteger from 'lodash/isInteger';
import React from 'react';
import { DeliveryMethod } from '../../api/models/deliveryMethod';
import {
    ATTENDEE_PAYER,
    FEE_COMPONENT_MAP,
    ONLINE_EVENT_TAX_CALCULATED_AT_CHECKOUT_ID,
    ORGANIZER_PAYER,
    PROCESSING_FEE_COMPONENT_NAME,
    ROYALTY_COMPONENT_NAME,
    SERVICE_FEE_COMPONENT_NAME,
    SERVICE_FEE_TEXT,
    TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE,
    TAX_PROCESSING_FEE_COMPONENT_NAME,
    TAX_SERVICE_FEE_COMPONENT_NAME,
} from '../../constants';
import {
    getTaxOnFeeComponents,
    PriceComponent,
    SelectedTicket,
    Ticket,
} from '../../models/tickets';
import { VirtualIncentive } from '../../models/user';
import { isOnlineEventTaxabilityBasedOnAttendeeLocationEnabled } from '../../selectors/splitio';
import { OrderNamespace } from '../../state/order';
import { formatSelectedQuantityByDeliveryMethodsForDisplay } from '../../utils/deliveryMethod';
import { getEventTaxName } from '../../utils/event';
import { getPromoCodeInformation } from '../../utils/promoCodes';
import { isDonationTicket } from '../../utils/tickets';
import { getSelectedVirtualIncentivesAggregatedTotal } from '../../utils/virtualIncentives';
import { BreakdownItems } from './ItemizedPaneContent';
import { OrderCosts } from './PaneContent';
/**
 * Since Pricing Service provides fee component name in format
 * `eventbrite.service_fee` or `royalty`. Here we are converting
 * the response format to display format `Service fee` or `Royalty`.
 * If no name is provided, this will default to a blank string.
 *
 * @param name - fee component name
 * @param value - value of fee component
 * @param currency
 * @param currencyFormat
 */
export const getFormattedFeeContent = (
    name = '',
    value: number,
    currency: string,
    currencyFormat: string,
) => {
    const formattedValue: string = formatMajorMoney(
        value,
        currency,
        currencyFormat,
    );
    const getFormattedContent =
        FEE_COMPONENT_MAP[name as keyof typeof FEE_COMPONENT_MAP] ||
        FEE_COMPONENT_MAP.default;

    const formattedContent =
        typeof getFormattedContent === 'function'
            ? getFormattedContent(name)
            : getFormattedContent;

    return {
        id: name,
        content: formattedContent.label,
        value: formattedValue,
    };
};

/**
 * Given array of feeComponents, here we are joining ESF and EPP components
 * into a single `Service Fee` display component. If there is a fee component
 * whose name is also `service fee` (case insensitive) it will also be included.
 * @param feeComponents
 */
export const joinServiceFeeComponents = (
    feeComponents: Array<PriceComponent> = [],
) => {
    const ret = [];
    const serviceFee = feeComponents.find((e) =>
        e.name.includes(SERVICE_FEE_COMPONENT_NAME),
    );
    const eppFee = feeComponents.find((e) =>
        e.name.includes(PROCESSING_FEE_COMPONENT_NAME),
    );
    const otherServiceFees = feeComponents.filter(
        (e) => e.name.toLowerCase().trim() === SERVICE_FEE_TEXT,
    );
    const otherFees = feeComponents.filter(
        ({ name }) =>
            !(
                name.includes(SERVICE_FEE_COMPONENT_NAME) ||
                name.includes(PROCESSING_FEE_COMPONENT_NAME)
            ) && name.toLowerCase().trim() !== SERVICE_FEE_TEXT,
    );

    if (serviceFee) {
        const finalServiceFee = { ...serviceFee };

        if (eppFee) {
            (finalServiceFee.value as number) += eppFee.value as number;
        }

        if (otherServiceFees) {
            (finalServiceFee.value as number) += otherServiceFees.reduce(
                (a, b) => a + (b.value as number),
                0,
            );
        }

        ret.push(finalServiceFee);
    }

    return ret.concat(otherFees);
};

export const getSummaryItems = ({
    selectedTickets,
    eventCurrency,
    currencyFormat,
    shouldHandleFreeTickets,
}: {
    selectedTickets: Array<
        Omit<SelectedTicket, 'category' | 'free'> &
            Partial<Pick<SelectedTicket, 'category' | 'free'>>
    >;
    eventCurrency: string;
    currencyFormat: string;
    shouldHandleFreeTickets: boolean;
}) =>
    selectedTickets.map(
        ({
            cost,
            free,
            id,
            name,
            selectedQuantity: quantity,
            color,
            category,
        }) => {
            let subtotal = 0;

            if (cost) {
                const freeAfterDiscount = !free && cost.value === 0;

                if (shouldHandleFreeTickets) {
                    subtotal = cost.value * quantity;
                } else {
                    subtotal =
                        free || freeAfterDiscount ? 0 : cost.value * quantity;
                }
            }
            const formattedSubtotal = formatMajorMoney(
                subtotal,
                eventCurrency,
                currencyFormat,
            );
            const value = formattedSubtotal;
            const content = name;

            return {
                content,
                quantity: quantity.toString(),
                id: id.toString(),
                value,
                color,
                donation: isDonationTicket({ category }),
            };
        },
    );

const getItemsForFullPriceAndSubtotal = ({
    currency,
    currencyFormat,
    discount,
    fees,
    fullPrice,
    taxes,
    isTransferredOrder,
}: {
    currency: string;
    currencyFormat: string;
    discount: number;
    fees: number;
    fullPrice: number;
    taxes?: number;
    isTransferredOrder: boolean;
}) => {
    if (!fullPrice) {
        return [];
    }

    const hasPriceModifiers = fees || taxes || discount;

    if (!hasPriceModifiers) {
        return [];
    }

    const fullPriceValue = formatMajorMoney(
        fullPrice,
        currency,
        currencyFormat,
    );
    const fullPriceColor = 'grey-800';

    if (discount) {
        return [
            {
                content: gettext('Full Price'),
                id: 'fullprice',
                majorValue: fullPrice,
                value: fullPriceValue,
                color: fullPriceColor,
            },
            {
                content: gettext('Discount'),
                id: 'discount',
                majorValue: fullPrice,
                value: `- ${formatMajorMoney(
                    discount,
                    currency,
                    currencyFormat,
                )}`,
                color: 'ui-green--contrast',
            },
        ];
    }

    return [
        {
            content: isTransferredOrder
                ? gettext('Price difference')
                : gettext('Subtotal'),
            id: 'subtotal',
            majorValue: fullPrice,
            value: fullPriceValue,
            color: fullPriceColor,
        },
    ];
};

const getItemsForEventbriteFee = ({
    fees,
    currency,
    currencyFormat,
    enableRefundFeeRetentionPolicy,
    feeComponents,
    shippingFee,
}: {
    fees: number;
    currency: string;
    currencyFormat: string;
    enableRefundFeeRetentionPolicy: boolean;
    feeComponents: Array<PriceComponent>;
    shippingFee?: {} | { value: number };
}) => {
    if (!fees && isEmpty(shippingFee)) {
        return [];
    }

    const feesItem: {
        content: GenericLazyString;
        id: string;
        value: string;
        majorValue: number;
        color: string;
        infoTooltipContent?: GenericLazyString;
        expansionContent?: Array<{
            id: string;
            content: string | GenericLazyString;
            value: string;
        }>;
    } = {
        content: gettext('Fees'),
        id: 'fees',
        value: formatMajorMoney(fees, currency, currencyFormat),
        majorValue: fees,
        color: 'grey-800',
    };

    if (enableRefundFeeRetentionPolicy) {
        feesItem.infoTooltipContent = gettext(
            "Eventbrite's fee is nonrefundable",
        );
    }

    if (!isEmpty(feeComponents) || !isEmpty(shippingFee)) {
        feesItem.expansionContent = getExpansionContent(
            feeComponents,
            shippingFee,
            currency,
            currencyFormat,
        );
    }

    return [feesItem];
};

const getTotalFees = (
    fees: number,
    shippingFee?: {} | { value: number } | undefined,
) => fees + (shippingFee && 'value' in shippingFee ? shippingFee.value : 0);

const joinShippingFees = (
    shippingFees?: Array<{ value: number }>,
): { name: string; value: number; majorValue: number } | {} => {
    if (!shippingFees || shippingFees.length === 0) {
        return {};
    }

    const totalValue = shippingFees.reduce(
        (total, fee) => total + fee.value,
        0,
    );

    return {
        name: 'eventbrite.shipping.item',
        value: totalValue,
        majorValue: Math.floor(totalValue * 100),
    };
};

const getExpansionContent = (
    feeComponents: Array<PriceComponent>,
    shippingFee: {} | { value: number } | undefined,
    currency: string,
    currencyFormat: string,
) => {
    const orderedFees = isEmpty(feeComponents)
        ? []
        : joinServiceFeeComponents(feeComponents);

    const expansionContent = orderedFees.map(({ name, value }) =>
        getFormattedFeeContent(name, value as number, currency, currencyFormat),
    );

    return !shippingFee || !('value' in shippingFee)
        ? expansionContent
        : [
              ...expansionContent,
              getFormattedFeeContent(
                  'eventbrite.shipping.item',
                  shippingFee.value,
                  currency,
                  currencyFormat,
              ),
          ];
};

type TaxItem = {
    content: GenericLazyString | string;
    id: string;
    value: GenericLazyString | string;
    color: string;
    infoTooltipContent?: GenericLazyString;
    majorValue: number;
    valueAdditionalClassNames?: string;
};

const getItemsForTaxes = ({
    taxes,
    eventTaxName,
    currency,
    currencyFormat,
    taxBeforeExemption,
}: {
    taxes?: number;
    eventTaxName: string;
    currency: string;
    currencyFormat: string;
    taxBeforeExemption?: number;
}) => {
    if (!taxes && !taxBeforeExemption) {
        return [];
    }

    const taxItem: TaxItem = {
        content: eventTaxName,
        id: 'taxes',
        value: formatMajorMoney(taxes, currency, currencyFormat),
        color: 'grey-800',
        majorValue: taxes || 0,
    };

    if (taxBeforeExemption) {
        taxItem.infoTooltipContent = gettext(
            'You are exempt from paying %(eventTaxName)s',
            { eventTaxName },
        );
        taxItem.value = formatMajorMoney(
            taxBeforeExemption,
            currency,
            currencyFormat,
        );
        taxItem.color = 'grey-800';
        taxItem.valueAdditionalClassNames =
            'eds-text--strikethrough eds-text-color--grey-500';
    }

    return [taxItem];
};

const getItemsForOnlineEventTaxes = (): TaxItem[] => [
    {
        content: gettext('Sales tax'),
        id: ONLINE_EVENT_TAX_CALCULATED_AT_CHECKOUT_ID,
        value: gettext('Calculated at checkout'),
        color: 'grey-800',
        majorValue: 0,
    },
];

const getItemsForInstallments = ({
    installments,
    currency,
    currencyFormat,
}: {
    installments: {
        numberOfInstallments: number;
        pricePerInstallment: number;
        installmentInterests: {
            TEA?: string;
            CFT?: string;
        };
    };
    currency: string;
    currencyFormat: string;
}) => {
    if (!installments) {
        return [];
    }

    const { numberOfInstallments, pricePerInstallment, installmentInterests } =
        installments;

    const items: Array<{
        content: GenericLazyString;
        id: string;
        value: string;
        majorValue: number | null;
        color?: string;
        infoTooltipContent?: GenericLazyString;
    }> = [];

    if (numberOfInstallments > 1) {
        items.push({
            content: gettext('%(numberOfInstallments)s x installments', {
                numberOfInstallments,
            }),
            id: 'installments',
            majorValue: null,
            value: formatMinorMoney(
                pricePerInstallment,
                currency,
                currencyFormat,
            ),
            color: 'grey-800 eds-l-pad-top-4 u-border-top u-border-color-ui-200',
        });
    }

    if (installmentInterests.TEA) {
        items.push({
            content: gettext('APR'),
            id: 'installments-tea',
            majorValue: null,
            value: installmentInterests.TEA,
            infoTooltipContent: gettext('Annual Percentage Rate'),
        });
    }

    if (installmentInterests.CFT) {
        items.push({
            content: gettext('TFC'),
            id: 'installments-cft',
            majorValue: null,
            value: installmentInterests.CFT,
            infoTooltipContent: gettext('Total Financial Cost'),
        });
    }

    return items;
};

const getItemsForDeliveryMethods = ({
    selectedDeliveryMethods,
    quantitySelectedDeliveryMethods,
    isRegEvent,
    shippingRates,
    currency,
    currencyFormat,
}: {
    selectedDeliveryMethods: Array<string>;
    quantitySelectedDeliveryMethods: Array<{
        selectedDelivery: DeliveryMethod;
        quantityDelivery: number;
    }>;
    isRegEvent: boolean;
    shippingRates: Array<{
        name: string;
        price: {
            value: number;
        };
    }>;
    currency: string;
    currencyFormat: string;
}) => {
    if (isEmpty(selectedDeliveryMethods)) {
        return [];
    }

    const deliveryMethods = quantitySelectedDeliveryMethods.map(
        (quantitySelectedDeliveryMethod, index) => (
            <span key={index} className="eds-g-cell eds-g-cell-1-1">
                {formatSelectedQuantityByDeliveryMethodsForDisplay(
                    quantitySelectedDeliveryMethod,
                    isRegEvent,
                )}
            </span>
        ),
    );

    const totalDeliveryFee = (shippingRates || [])
        .filter((rate) => selectedDeliveryMethods.includes(rate.name))
        .reduce((a, b) => a + b.price.value, 0);

    return [
        {
            content: gettext('Delivery'),
            subcontent: deliveryMethods,
            id: 'deliveryMethods',
            color: 'grey-800',
            majorValue: totalDeliveryFee,
            value: formatMajorMoney(totalDeliveryFee, currency, currencyFormat),
        },
    ];
};

const getItemsForVirtualIncentives = ({
    currency,
    currencyFormat,
    selectedVirtualIncentives,
    unformattedOrderCost,
}: {
    currency: string;
    currencyFormat: string;
    selectedVirtualIncentives: Array<VirtualIncentive>;
    unformattedOrderCost: number;
}) => {
    if (isEmpty(selectedVirtualIncentives)) {
        return [];
    }
    let totalUsedCredits = getSelectedVirtualIncentivesAggregatedTotal(
        selectedVirtualIncentives,
    );

    if (unformattedOrderCost < totalUsedCredits) {
        totalUsedCredits = unformattedOrderCost;
    }

    return [
        {
            content: gettext('Credits'),
            id: 'selectedVirtualIncentives',
            color: 'grey-800',
            majorValue: unformattedOrderCost,
            value: `- ${formatMajorMoney(
                totalUsedCredits,
                currency,
                currencyFormat,
            )}`,
        },
    ];
};

export const getBreakdownItems = ({
    currency,
    currencyFormat,
    discount,
    enableRefundFeeRetentionPolicy,
    isOnlineEvent,
    areTaxesCalculatedAtCheckout,
    eventId,
    eventCountry,
    eventTaxName,
    feeComponents,
    fees,
    fullPrice,
    installments,
    isTransferredOrder,
    isRegEvent,
    quantitySelectedDeliveryMethods,
    selectedDeliveryMethods,
    shippingFees,
    shippingRates,
    taxes,
    taxBeforeExemption,
    selectedVirtualIncentives,
    unformattedOrderCost,
    orderCosts,
}: {
    currency: string;
    currencyFormat: string;
    discount: number;
    enableRefundFeeRetentionPolicy: boolean;
    isOnlineEvent: boolean;
    areTaxesCalculatedAtCheckout: boolean;
    eventId: string;
    eventCountry: string;
    eventTaxName: string;
    feeComponents: Array<PriceComponent>;
    fees: number;
    fullPrice: number;
    installments: {
        numberOfInstallments: number;
        pricePerInstallment: number;
        installmentInterests: {
            TEA?: string;
            CFT?: string;
        };
    };
    isTransferredOrder: boolean;
    isRegEvent: boolean;
    quantitySelectedDeliveryMethods: Array<{
        selectedDelivery: DeliveryMethod;
        quantityDelivery: number;
    }>;
    selectedDeliveryMethods: Array<string>;
    shippingFees: Array<{ value: number }>;
    shippingRates: Array<{
        name: string;
        price: {
            value: number;
        };
    }>;
    taxes?: number | undefined;
    taxBeforeExemption?: number;
    selectedVirtualIncentives: Array<VirtualIncentive>;
    unformattedOrderCost: number;
    orderCosts?: unknown; // TODO: Find proper value
}) => {
    const shippingFee = joinShippingFees(shippingFees);
    const totalFeesItems = getTotalFees(
        fees,
        isTransferredOrder || !isEmpty(orderCosts) ? undefined : shippingFee,
    );

    const fullPriceAndSubtotalItems = getItemsForFullPriceAndSubtotal({
        fullPrice,
        fees,
        taxes,
        discount,
        currency,
        currencyFormat,
        isTransferredOrder,
    });

    // For Members, fees show on the first ticket selection step
    // After the user clicks Checkout, there is no fee and the
    // discount is reflected in the price and feeComponents
    const eventbriteFeesItems = getItemsForEventbriteFee({
        fees: totalFeesItems,
        currency,
        currencyFormat,
        enableRefundFeeRetentionPolicy,
        feeComponents,
        shippingFee,
    });

    const shouldGetItemsForOnlineEventTaxes =
        isOnlineEventTaxabilityBasedOnAttendeeLocationEnabled(
            eventId,
            eventCountry,
        ) &&
        isOnlineEvent &&
        areTaxesCalculatedAtCheckout;

    const taxesItems: TaxItem[] =
        shouldGetItemsForOnlineEventTaxes && fullPrice > 0
            ? getItemsForOnlineEventTaxes()
            : getItemsForTaxes({
                  taxes,
                  eventTaxName,
                  currency,
                  currencyFormat,
                  taxBeforeExemption,
              });

    const installmentItems = getItemsForInstallments({
        installments,
        currency,
        currencyFormat,
    });

    const deliveryMethodsItems = getItemsForDeliveryMethods({
        selectedDeliveryMethods,
        quantitySelectedDeliveryMethods,
        isRegEvent,
        shippingRates,
        currency,
        currencyFormat,
    });

    const virtualIncentivesItems = getItemsForVirtualIncentives({
        currency,
        currencyFormat,
        selectedVirtualIncentives,
        unformattedOrderCost,
    });

    return [
        ...fullPriceAndSubtotalItems,
        ...eventbriteFeesItems,
        ...taxesItems,
        ...installmentItems,
        ...deliveryMethodsItems,
        ...virtualIncentivesItems,
    ];
};

const ticketCostsReducer = <
    MemoizedData extends {
        fullPrice: number;
        discount: number;
        currency: string;
        fees: number;
        taxes: number;
        taxComponents?: Array<PriceComponent>;
        taxBeforeExemption?: number;
    },
>(
    memo: MemoizedData,
    ticket: Ticket,
): MemoizedData => {
    const costs = memo;
    const {
        cost,
        currency,
        displayShippingFee,
        fee,
        free,
        selectedQuantity,
        tax,
        taxComponents = [],
        taxWithoutShipping,
    } = ticket;

    // Calculate costs for paid tickets using variants information
    if (!free && cost) {
        const promoCodeInformation = getPromoCodeInformation({ ticket });

        const { fullPrice, discount } =
            'originalCost' in promoCodeInformation &&
            promoCodeInformation.originalCost &&
            promoCodeInformation.amountOff
                ? {
                      fullPrice: promoCodeInformation.originalCost.value,
                      // In cases where the amountOff is greater than the
                      // original cost of the ticket, record discount value
                      // up to the original cost.
                      discount: Math.min(
                          promoCodeInformation.amountOff.value,
                          promoCodeInformation.originalCost.value,
                      ),
                  }
                : {
                      fullPrice: cost.value,
                      discount: 0,
                  };

        if (selectedQuantity > 0) {
            const isAbsorbedTax = !!getTaxOnFee(taxComponents);
            const feeValue = isAbsorbedTax ? fee.value + tax.value : fee.value;
            const taxValue = getTaxValue({
                displayShippingFee,
                selectedQuantity,
                tax,
                isAbsorbedTax,
                taxWithoutShipping,
            });

            costs.fullPrice += fullPrice * selectedQuantity;
            costs.discount += discount * selectedQuantity;
            costs.currency = currency;
            costs.fees += feeValue * selectedQuantity;
            costs.taxes += taxValue;
            costs.taxComponents = taxComponents;
            costs.taxBeforeExemption = tax.taxBeforeExemption;
        }

        // If currency is undefined, then set it.
        if (!costs.currency && cost.currency) {
            costs.currency = cost.currency;
        }
    }

    return costs;
};

/**
 * Given the fee components for selectedTickets adjusted by selected
 * quantity, we are combining all fee components with the same name to
 * be presented in the fee breakdown.
 *
 * @param memo - Existing fee components
 * @param components - New components to add
 */
const feeBreakdownComponentsReducer = (
    memo: Array<{ name: string; value: number }>,
    components: Array<{ name: string; value: number }>,
) => {
    const existing = components;
    const feeArray = memo;
    let ret = null;

    if (isEmpty(existing)) {
        return feeArray;
    }

    const itemComponents = existing.reduce<
        Array<{ name: string; value: number }>
    >((acc, element) => {
        const index = acc.findIndex(
            ({ name }) =>
                name.toLowerCase().trim() === element.name.toLowerCase().trim(),
        );
        let el = element;

        if (index !== -1) {
            el = acc[index];
            (el.value as number) += element.value as number;
            acc.splice(index, 1);
        }
        acc.push(el);
        return acc;
    }, []);

    if (isEmpty(feeArray)) {
        return itemComponents;
    }

    // Merge the itemComponents into the feeArray
    ret = feeArray.map((element) => {
        const itemComponent = itemComponents.find(
            (e) => e.name === element.name,
        );
        let el = element;

        if (itemComponent) {
            const index = itemComponents.findIndex(
                (e) => e.name === element.name,
            );

            el = itemComponent;
            (el.value as number) += element.value as number;
            itemComponents.splice(index, 1);
        }
        return el;
    });

    if (!isEmpty(itemComponents)) {
        // add rempaining item components to feeArray
        ret = [...ret, ...itemComponents];
    }

    return ret;
};

/**
 * Given the all fee components for selectedTickets, we are filtering
 * the components with value, payer ateendee and adjusting by selected
 * quantity. These components will be present in the fee breakdown.
 *
 * @param selectedQuantity - Tickets selected by the user.
 * @param taxComponents - Tax components of the tickets selected.
 * @param acc - Array to return the fee components.
 * @param feeComponents - Fee component element to analize.
 */
const feeComponentsReducer = (
    selectedQuantity: number,
    taxComponents: Array<PriceComponent>,
    acc: Array<{ name: string; value: number }>,
    { name, value, groupName, payer }: PriceComponent,
) => {
    if (
        payer === ORGANIZER_PAYER ||
        (name === ROYALTY_COMPONENT_NAME && !(value as { value: number }).value)
    ) {
        return acc;
    }
    let componentName = name;
    let adjustedValue = (value as { value: number }).value * selectedQuantity;
    // We don't want to show taxes as a separate row if they are just taxes on the fees
    const taxOnFee = getTaxOnFee(taxComponents);

    if (groupName && name !== groupName) {
        componentName = groupName;
    }

    if (taxOnFee && componentName === SERVICE_FEE_COMPONENT_NAME) {
        // For reserved seating events, value is not nested
        const taxValue = isInteger(taxOnFee.value)
            ? (taxOnFee.value as number)
            : (taxOnFee.value as { value: number }).value;

        adjustedValue += taxValue * selectedQuantity;
    }

    return [
        ...acc,
        {
            name: componentName,
            value: adjustedValue,
        },
    ];
};

const hasTaxComponent = (
    payer: string,
    value: { value: number },
    groupName: string,
) =>
    payer === ATTENDEE_PAYER &&
    value.value &&
    [
        TAX_SERVICE_FEE_COMPONENT_NAME,
        TAX_PROCESSING_FEE_COMPONENT_NAME,
    ].includes(groupName);

/**
 * Given the all tax components for selectedTickets, we are filtering all the components
 * with value, payer ateendee, group name 'eventbrite.service_fee.tax' or 'eventbrite.payment_fee_v2.tax'
 * and adjusting by selected quantity. These components will be present in the fee breakdown.
 *
 * @param selectedQuantity - Tickets selected by the user.
 * @param acc - Array to return the fee components.
 * @param taxComponents - Tax component element to analize.
 */
const taxComponentsReducer = (
    selectedQuantity: number,
    adjustedComponents: Array<{ name: string; value: number }>,
    { value, groupName, payer }: PriceComponent & { value: { value: number } },
) => {
    if (!hasTaxComponent(payer, value, groupName)) {
        return adjustedComponents;
    }

    return [
        ...adjustedComponents,
        {
            name: SERVICE_FEE_COMPONENT_NAME,
            value: value.value * selectedQuantity,
        },
    ];
};

/**
 * Given selectedTickets, for each ticket we adjust the fee components
 * for the selected quantity, and combine all ticket fee components with
 * the same name to be presented in fee breakdown.
 *
 * @param selectedTickets - tickets selected by the user.
 * @return fee components adjusted for selected quantity and
 * combined by name
 */
export const getTicketFeeComponents = (selectedTickets: Array<Ticket>) => {
    const components = selectedTickets.map(
        ({ feeComponents, selectedQuantity, taxComponents = [] }) => {
            const adjustedComponents = feeComponents
                ? feeComponents.reduce<Array<{ name: string; value: number }>>(
                      (accum, component) =>
                          feeComponentsReducer(
                              selectedQuantity,
                              taxComponents,
                              accum,
                              component,
                          ),
                      [],
                  )
                : [];

            return isEmpty(taxComponents)
                ? adjustedComponents
                : taxComponents.reduce(
                      (accum, component) =>
                          taxComponentsReducer(
                              selectedQuantity,
                              accum,
                              component as PriceComponent & {
                                  value: { value: number };
                              },
                          ),
                      adjustedComponents,
                  );
        },
    );
    const ret = components.reduce<Array<{ name: string; value: number }>>(
        feeBreakdownComponentsReducer,
        [],
    );

    return ret;
};

/**
 * Since basket_cost is not working we are calculating values
 * from the list of selected tickets.
 *
 * @param selectedTickets - tickets selected by the user.
 * @param hasFeeBreakdown - If should show fee breakdown.
 * @returns selected tickets total fees, taxes, currency and subtotal.
 */
export const getTicketsCosts = (
    selectedTickets: Array<Ticket>,
    hasFeeBreakdown: boolean,
) => {
    const feeComponents = hasFeeBreakdown
        ? getTicketFeeComponents(selectedTickets)
        : [];

    return selectedTickets.reduce<{
        currency: string;
        fees: number;
        feeComponents: Array<{ name: string; value: number } | PriceComponent>;
        fullPrice: number;
        discount: number;
        taxes: number;
        taxComponents: [];
    }>((accum, item) => ticketCostsReducer(accum, item), {
        currency: '',
        fees: 0,
        feeComponents,
        fullPrice: 0,
        discount: 0,
        taxes: 0,
        taxComponents: [],
    });
};

/**
 * Once an order is created. All the price cost info is returned with
 * API response. We can extract the ticketCosts with this orderCosts.
 *
 * @param orderCosts - costs info of the order created.
 * @return selected tickets total fees, taxes, currency and subtotal.
 */
// TODO: instead of passing orderCosts, compose with a selector
export const getTicketsCostsByOrder = (
    orderCosts: OrderNamespace['costs'],
    hasFeeBreakdown: boolean,
) => {
    // TODO: Each item destructured here should be a separate selector in `selectors/order.js`
    // Example: `getOrderCostsPriceBeforeDiscount`, `getOrderCostFeeComponents`, etc.
    // Once this is done, then these default value logic will be handled by the selectors
    const {
        priceBeforeDiscount = { currency: '', value: 0 },
        discountAmount,
        displayTax,
        displayPrice,
        displayFee: { value: fees } = { value: 0 },
        tax: { taxBeforeExemption } = {},
        feeComponents = [],
        taxComponents = [],
    } = orderCosts;

    const currency = priceBeforeDiscount.currency;
    const fullPrice = taxBeforeExemption
        ? displayPrice.value
        : priceBeforeDiscount.value;

    let discount = 0;
    let taxes = 0;

    const hasFeeTaxIncludable = !!taxComponents.find(
        (component) =>
            component.base === TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE,
    );
    const hasTaxOnFee = !!taxComponents.find(
        (component) =>
            [
                TAX_SERVICE_FEE_COMPONENT_NAME,
                TAX_PROCESSING_FEE_COMPONENT_NAME,
            ].includes(component.groupName) &&
            component.payer == ATTENDEE_PAYER,
    );

    let feeItems: Array<PriceComponent> = [];

    if (hasFeeBreakdown) {
        feeItems = feeComponents;
    }

    if (discountAmount) {
        discount = discountAmount.value;
    }

    if (hasTaxOnFee) {
        feeItems = hasFeeBreakdown
            ? getTaxOnFeeComponents(feeItems, taxComponents)
            : [];
    }

    if (displayTax && !hasFeeTaxIncludable) {
        taxes = displayTax.tax.value;
    }

    return {
        currency,
        discount,
        fees,
        feeComponents: feeItems,
        fullPrice,
        taxComponents,
        taxes,
        taxBeforeExemption,
    };
};

const getTaxOnFee = (taxComponents: Array<PriceComponent>) =>
    taxComponents.find(
        (component) =>
            component.base === TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE,
    );

const getTaxValue = ({
    displayShippingFee,
    selectedQuantity,
    tax,
    isAbsorbedTax,
    taxWithoutShipping,
}: {
    displayShippingFee?: {
        value: number;
    };
    selectedQuantity: number;
    tax: {
        value: number;
    };
    isAbsorbedTax: boolean;
    taxWithoutShipping?: {
        value: number;
    };
}) => {
    const taxValue = isAbsorbedTax ? 0 : tax.value;
    const taxWithoutShippingValue =
        displayShippingFee?.value && !isAbsorbedTax
            ? taxWithoutShipping?.value ?? 0
            : 0;

    return displayShippingFee?.value
        ? taxValue + taxWithoutShippingValue * (selectedQuantity - 1)
        : taxValue * selectedQuantity;
};

export const ticketHasGtsOnTax = (ticket: Ticket) => ticket.hasGtsTax;

export const getTaxName = (
    ticketsById: Record<string, Ticket>,
    selectedTicketsOnSale: Array<Ticket>,
): string => {
    // Not need to check on the selected tickets because the GTS tax info is shared among all the ticket classes
    const hasGtsTax = Object.values(ticketsById).some(ticketHasGtsOnTax);
    const customEventTaxName = getEventTaxName(selectedTicketsOnSale);

    if (hasGtsTax) {
        return gettext('Sales Tax').toString();
    }
    if (customEventTaxName !== undefined) {
        return customEventTaxName;
    }
    return gettext('Tax').toString();
};

const _ticketsIncludeTaxOnFees = (tickets: Array<Ticket>) =>
    tickets
        .flatMap((ticket) => ticket.taxComponents)
        .filter(
            (ticket) =>
                ticket &&
                (ticket.base === TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE ||
                    ([
                        TAX_SERVICE_FEE_COMPONENT_NAME,
                        TAX_PROCESSING_FEE_COMPONENT_NAME,
                    ].includes(ticket.groupName) &&
                        ticket.payer == ATTENDEE_PAYER)),
        );

const _ticketsIncludeGtsTax = (tickets: Array<Ticket>) =>
    tickets.some(
        (ticket) => ticket.tax && ticket.tax.value === 0 && ticket.hasGtsTax,
    );

const _orderCostsIncludeNotDisplayedGtsTax = (orderCosts: OrderCosts) =>
    orderCosts.hasGtsTax && !orderCosts.displayTax;

const _areTaxesCalculatedAtCheckout = (breakdownItems?: BreakdownItems) =>
    breakdownItems &&
    breakdownItems.some(
        (item) => item.id === ONLINE_EVENT_TAX_CALCULATED_AT_CHECKOUT_ID,
    );

// TODO: Resolve duplicate logic in `components/reserved/utils.js:getTotalCostInfo`
// This can be refactored into a selector in `selectors/tickets.js`
export const getTotalCostInfo = ({
    tickets,
    breakdownItems,
    orderCosts,
}: {
    tickets: Array<Ticket>;
    breakdownItems?: BreakdownItems;
    orderCosts?: OrderCosts;
}) => {
    const totalCost = getTicketsCosts(tickets, true);

    if (
        totalCost?.fullPrice > 0 &&
        _areTaxesCalculatedAtCheckout(breakdownItems)
    ) {
        return gettext('Before Tax');
    }

    if (orderCosts) {
        if (_orderCostsIncludeNotDisplayedGtsTax(orderCosts)) {
            return gettext('Incl. sales tax');
        }
        return null;
    }

    if (_ticketsIncludeGtsTax(tickets)) {
        return gettext('Incl. sales tax');
    }

    if (!isEmpty(_ticketsIncludeTaxOnFees(tickets))) {
        return gettext('Price includes tax');
    }

    return null;
};
